// Ref-counted "make this element a positioned containing block" lease.
//
// Our anchored popovers (AnchoredPopover here, plus the analytics icon-menu and
// schedule drawer, which import this module) portal INTO their nearest
// scroll container and position themselves `absolute` in its content space. That
// only anchors correctly if the container is itself positioned, so we flip a
// `static` container to `position: relative`. The hazard is that ONE shared
// container (usually <main>) is mutated by MANY popover instances: if each
// instance independently restored the container on its own close, popover B
// closing would yank the container back to `static` while popover A is still
// anchored to it (A visibly jumps), and a component unmounting while open would
// leak `relative` forever.
//
// This lease centralises the mutation. The FIRST acquire flips the container and
// records its prior inline value; every acquire increments a count; only the
// LAST release restores the recorded value. A WeakMap keys the state per element
// (no leak when the container is GC'd). Callers must pair exactly one release
// with each acquire — the components guard this with a per-instance `held` ref so
// interrupted exits / StrictMode double-invokes can't double-count.

interface Lease {
	count: number
	/** The container's inline `style.position` before we first touched it. */
	prevInline: string
	/** Whether this lease actually changed the inline style (was `static`). */
	mutated: boolean
}

const leases = new WeakMap<HTMLElement, Lease>()

/** Ensure `el` is a positioned containing block; ref-counted. */
export const acquirePositioned = (el: HTMLElement): void => {
	const existing = leases.get(el)
	if (existing) {
		existing.count += 1
		return
	}
	const prevInline = el.style.position
	const mutated = getComputedStyle(el).position === 'static'
	if (mutated) el.style.position = 'relative'
	leases.set(el, { count: 1, prevInline, mutated })
}

/** Release one lease; restores the original inline `position` on the last one. */
export const releasePositioned = (el: HTMLElement): void => {
	const lease = leases.get(el)
	if (!lease) return
	lease.count -= 1
	if (lease.count > 0) return
	// Last holder let go — restore only if we were the ones who changed it, and
	// only if nothing else has since overridden our inline `relative`.
	if (lease.mutated && el.style.position === 'relative') el.style.position = lease.prevInline
	leases.delete(el)
}
